import { useState, useEffect, useRef } from 'react';
import { Box, Link, Theme, Typography, IconButton, Fade } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AnnouncementBanner } from 'sharedComponents/Banner/AnnouncementBanner';
import { ConnectMatchBannerDataTestIds } from 'data-testids/ConnectDataTestIds';
import { useConnectContext } from 'features/Connect/features/Context/ConnectContextProvider';
import { styled } from '@mui/material/styles';
import { CONNECT_BANNER_MIN_HEIGHT } from '../Banner/constants';
import auth from 'utils/auth';
import CloseIcon from '@mui/icons-material/Close';
import { ConnectMatchesNotificationBannerProps } from 'types/connectTypes';

export const ConnectMatchesNotificationBanner: React.FC<ConnectMatchesNotificationBannerProps> = ({
  onDismiss,
}) => {
  const { isUserAuthenticated, isFetchingMatches, candidateMatches } = useConnectContext();
  const [shouldRender, setShouldRender] = useState(false);
  const user = auth.getUser();
  const totalPendingMatches = candidateMatches?.pending?.length;
  const theme = useTheme();
  const prevAuthRef = useRef(isUserAuthenticated);
  const isEligible = !!user?.profile && totalPendingMatches > 0;
  const [open, setOpen] = useState(true);

  useEffect(() => {
    prevAuthRef.current = isUserAuthenticated;
  }, [isUserAuthenticated, user]);

  useEffect(() => {
    const isAuthenticated = !!user?.profile;
    if (isAuthenticated && !isFetchingMatches && totalPendingMatches > 0) {
      setShouldRender(true);
    } else {
      setShouldRender(false);
    }
  }, [totalPendingMatches, user, isFetchingMatches]);

  if (!isEligible) {
    return null;
  }

  const boldTextContent = `${totalPendingMatches} principals want to connect with you 🎉`;
  const regularTextContent =
    'Find your weekly matches in your candidate portal—click your avatar in the top right or click';

  const handleClose = () => setOpen(false);

  const content = (
    <TextContainer>
      <BoldText sx={{ typographyStyles }}>{boldTextContent}</BoldText>
      <RegularText sx={{ typographyStyles }}>
        {regularTextContent}{' '}
        <BannerLink
          data-testid={ConnectMatchBannerDataTestIds.REDIRECT_LINK}
          href={`/candidate/matches`}
        >
          here
        </BannerLink>
        .
      </RegularText>
      <CloseButtonWrapper>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ ml: 1, color: '#FFF' }}
          data-testid={ConnectMatchBannerDataTestIds.CLOSE_BUTTON}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </CloseButtonWrapper>
    </TextContainer>
  );

  return (
    <Fade in={open} onExited={onDismiss} timeout={300} unmountOnExit>
      <Box>
        <AnnouncementBanner
          body={shouldRender ? content : <></>}
          dataTestId={ConnectMatchBannerDataTestIds.MATCHES_ANNOUNCEMENT_BANNER}
          sx={bannerStyles(theme)}
        />
      </Box>
    </Fade>
  );
};

// Styles
const bannerStyles = (theme: Theme) => ({
  position: 'sticky',
  top: '80px',
  zIndex: 1100,
  display: 'flex',
  direction: 'row',
  width: '100%',
  minHeight: CONNECT_BANNER_MIN_HEIGHT,
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: theme.palette.purple.dark,
  textWrap: 'none',

  [theme.breakpoints.down('lg')]: {
    top: '64px',
    padding: theme.spacing(1, 2),
    textAlign: 'center',
    textWrap: 'wrap',
  },
});

const TextContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
}));

const typographyStyles = (theme: Theme) => ({
  color: '#FFF',
  textAlign: 'center',
  fontSize: theme.typography.heading7.fontSize,
  fontStyle: 'normal',
});

const BoldText = styled(Typography)(({ theme }) => ({
  color: theme.palette.white.main,
  textAlign: 'center',
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h3.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMediumBold,
  lineHeight: '30px',
}));

const RegularText = styled(Typography)(({ theme }) => ({
  color: theme.palette.white.main,
  textAlign: 'center',
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h3.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '26px',
}));

const BannerLink = styled(Link)(({ theme }) => ({
  color: theme.palette.white.main,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.h3.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '26px',
  textDecoration: 'underline',
  textDecorationStyle: 'solid',
  textDecorationSkipInk: 'none',
  textUnderlineOffset: 'auto',
  textUnderlinePosition: 'from-font',
}));

const CloseButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));
